import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';

const LanguageSelector = ({ isOpen: propIsOpen, onClose }) => {
  const { i18n, t } = useTranslation();
  const [isOpen, setIsOpen] = useState(propIsOpen || false);
  const [currentLang, setCurrentLang] = useState(i18n.language || 'en');

  // Sync with prop changes
  useEffect(() => {
    if (propIsOpen !== undefined) {
      setIsOpen(propIsOpen);
    }
  }, [propIsOpen]);

  // Sync with i18n changes
  useEffect(() => {
    setCurrentLang(i18n.language || 'en');
  }, [i18n.language]);

  // Handle language change
  const handleLanguageChange = async (newLang) => {
    if (newLang === currentLang) return;
    
    console.log('Changing language to:', newLang);
    
    try {
      // Close modal first
      handleClose();
      
      // Change language
      await i18n.changeLanguage(newLang);
      
      // Update local state
      setCurrentLang(newLang);
      
      // Save to localStorage
      localStorage.setItem('i18nextLng', newLang);
      
      console.log('Language changed successfully to:', newLang);
    } catch (error) {
      console.error('Error changing language:', error);
    }
  };

  // Handle modal close
  const handleClose = () => {
    setIsOpen(false);
    if (onClose) {
      onClose();
    }
  };

  // Close modal when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isOpen && !event.target.closest('.language-modal')) {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'unset';
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape' && isOpen) {
        handleClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const languages = [
    { code: 'en', name: 'English', nativeName: 'English', flag: '🇺🇸', country: 'United States' },
    { code: 'de', name: 'German', nativeName: 'Deutsch', flag: '🇩🇪', country: 'Germany' },
    { code: 'fr', name: 'French', nativeName: 'Français', flag: '🇫🇷', country: 'France' }
  ];

  const currentLanguage = languages.find(lang => lang.code === currentLang) || languages[0];

  return (
    <>
      {/* Language Selector Button - Only show if not controlled by props */}
      {propIsOpen === undefined && (
        <button 
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-gray-700 hover:text-yellow-600 hover:bg-yellow-50 dark:text-gray-300 dark:hover:text-white dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:ring-opacity-50 rounded-lg transition-all duration-200"
          aria-label="Select Language"
        >
          <span className="text-lg">🌐</span>
          <span>{currentLanguage.code.toUpperCase()}</span>
          <svg 
            className={`w-4 h-4 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      )}

      {/* Modal Overlay - Using Portal */}
      {isOpen && createPortal(
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center backdrop-blur-sm"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 2147483647
          }}
        >
          {/* Modal Content */}
          <div 
            className="language-modal bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-6 w-80 max-w-[90vw] max-h-[80vh] overflow-auto border border-yellow-300 transform transition-all duration-300 scale-100"
            style={{
              animation: 'modalSlideIn 0.3s ease-out'
            }}
          >
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-3">
                <span className="text-2xl">🌐</span>
                <h3 className="text-lg font-bold text-gray-800 dark:text-gray-200">
                  {t('language_selector.title', 'Select Language')}
                </h3>
              </div>
              <button 
                onClick={handleClose}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors p-1 rounded"
                aria-label="Close"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Language Options */}
            <div className="space-y-2">
              {languages.map((language) => (
                <button
                  key={language.code}
                  onClick={() => handleLanguageChange(language.code)}
                  className={`w-full flex items-center p-3 rounded-xl transition-all duration-200 text-left ${
                    currentLang === language.code
                      ? 'bg-yellow-50 border-2 border-yellow-400 text-yellow-800 shadow-sm'
                      : 'border-2 border-gray-200 hover:border-yellow-300 hover:bg-yellow-50 text-gray-700 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-700'
                  }`}
                >
                  <span className="text-3xl mr-4">{language.flag}</span>
                  <div className="flex-1">
                    <div className="font-semibold">{language.nativeName}</div>
                    <div className="text-sm opacity-70">{language.name} - {language.country}</div>
                  </div>
                  {currentLang === language.code && (
                    <div className="flex items-center justify-center w-6 h-6 bg-yellow-400 rounded-full ml-2">
                      <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                  )}
                </button>
              ))}
            </div>

            {/* Footer */}
            <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-600">
              <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
                {t('language_selector.instruction', 'Click outside or press ESC to close')}
              </p>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* CSS Animation */}
      <style>{`
        @keyframes modalSlideIn {
          from {
            opacity: 0;
            transform: scale(0.95) translateY(-20px);
          }
          to {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }
      `}</style>
    </>
  );
};

export default LanguageSelector;
